import classNames from 'classnames';
import React, { useMemo } from 'react';

import { format as formatDate } from '@eika-infrastruktur/date';
import { truncateName, getTruncatedNameWithSuffix } from '@eika-infrastruktur/format';
import { RowButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { AttentionLabel, Label, SuccessLabel } from '@eika/label';

import useTransactionActor from '../../hooks/useTransactionActor';
import useTranslation, { format } from '../../hooks/useTranslation';
import TranslationsConfigType from '../../translations/translationType';
import { FormattedFuturePayment } from '../../utils/mapper/futurepaymentsUtil';
import PaymentDescription from './components/PaymentDescription/PaymentDescription';

import './FuturePaymentsListMonth.scss';

const getAriaLabel = (payment: FormattedFuturePayment, paymentTypeText: string) => {
    const date = formatDate(payment.date, 'dd. MMMM yyyy');
    return `${paymentTypeText} kr ${payment.amountFormatted} ${date}`;
};

const getPaymentSubStatusLabel = (payment: FormattedFuturePayment, translated: TranslationsConfigType) => {
    if (payment.paymentSubStatus === 'INSUFICIENT_FUNDS' && payment.paymentType === 'TRANSFER' && payment.amount > 0) {
        return translated.insufficientFundsFromOwnAccount;
    }
    return payment.paymentSubStatus ? translated.paymentSubStatus[payment.paymentSubStatus] : undefined;
};

interface Props {
    payment: FormattedFuturePayment;
    onClick: (futurePayment: FormattedFuturePayment) => void;
    showFromAccount?: boolean;
}

const FuturePaymentListMonthElement: React.FC<Props> = ({ payment, onClick, showFromAccount = false }) => {
    const translated = useTranslation();
    const isDeposit = payment.amount > 0;
    const { transactionActor: receiver } = useTransactionActor(
        isDeposit ? payment.fromAccount : payment.toAccount,
        payment.paymentType !== 'FOREIGN_PAYMENT' && !isDeposit,
    );

    const hasOwner = useMemo(() => payment.owner && !payment.owner.equalToUserLoggedOn, [payment.owner]);
    const ariaLabel = useMemo(
        () => getAriaLabel(payment, translated.futurePaymentType[payment.paymentType]),
        [payment, translated],
    );
    const paymentStatusLabel = useMemo(() => getPaymentSubStatusLabel(payment, translated), [payment, translated]);

    const firstApprover = payment.approvers.find((x) => x.firstApprover === true);
    const approvedBy = payment.approvers.filter((x) => !x.firstApprover);

    const showOwner = hasOwner && showFromAccount && !firstApprover && approvedBy.length === 0;
    const hasLabel = paymentStatusLabel || showOwner;
    return (
        <RowButton
            className={classNames({ 'future-payments-list-month__deposit': isDeposit })}
            onClick={() => onClick(payment)}
            aria-label={ariaLabel}
        >
            <div
                className={classNames('future-payments-list-month__row', {
                    'future-payments-list-month__row--has-label': hasLabel,
                })}
            >
                {hasLabel && (
                    <div className="future-payments-list-month-label">
                        {paymentStatusLabel && <Label text={paymentStatusLabel} size="small" type="attention" />}
                        {showOwner && (
                            <Label
                                size="small"
                                type="info"
                                icon={IconTypes.PROFIL_24}
                                text={getTruncatedNameWithSuffix(
                                    payment.owner?.name,
                                    translated.futurePaymentOwnerSuffix,
                                )}
                            />
                        )}
                    </div>
                )}
                <span className="future-payments-list-month__date" data-testid="future-payment-date">
                    {payment.dateFormatted}
                </span>
                <PaymentDescription receiver={receiver} />
                <span className="future-payments-list-month__amount" data-testid="future-payment-amount">
                    {payment.amountFormatted}
                </span>
                {payment.approvers.length > 1 && firstApprover ? (
                    <span className="future-payments-list-month__approved-by" data-testid="future-payment-approvers">
                        <AttentionLabel
                            size="small"
                            text={format(translated.futurePayments.paidBy, truncateName(firstApprover.name))}
                        />
                        {approvedBy.map((approver) => (
                            <SuccessLabel
                                key={approver.name}
                                size="small"
                                text={format(
                                    translated.futurePayments.approvedBy,
                                    truncateName(approver.name),
                                    formatDate(approver.dateOfApproval, 'dd.MM.yy'),
                                )}
                                icon={IconTypes.USP_CHECKMARK_24}
                            />
                        ))}
                    </span>
                ) : (
                    <span className="future-payments-list-month__payment-type" data-testid="future-payment-type">
                        {translated.futurePaymentType[payment.paymentType]}
                    </span>
                )}

                {showFromAccount && payment.fromAccount.alias && (
                    <span className="future-payments-list-month__from-account">{payment.fromAccount.alias}</span>
                )}
                {payment.balance && (
                    <span
                        className={classNames('future-payments-list-month__balance', {
                            'not-sufficient-balance': payment.notSufficientBalance,
                            'no-from-account': !showFromAccount || !payment.fromAccount.alias,
                        })}
                    >
                        {format(translated.balanceAfterPayment, payment.balance)}
                    </span>
                )}
            </div>
        </RowButton>
    );
};

export default FuturePaymentListMonthElement;
